import type { ColorScale } from '../colorScale';

/**
 * Samples across a continuous ramp, where there is no boundary to land on.
 *
 * 256 is enough that a gradient shows no stepping at any plausible chart size.
 */
export const RAMP_TEXTURE_WIDTH = 256;

/**
 * Texels for one scale.
 *
 * A whole multiple of the band count when banded, so every boundary lands on a texel edge instead
 * of up to half a texel away from it. 256 does not divide by nine - the persisted default - which
 * put the GPU surface's steps in different places from the software path's, and left the bands
 * themselves unequal in width: 28, 29, 28, 29 texels for a nominal 28.44. `rampLookup.test.ts`
 * pins the same arithmetic for the contour chart's own lookup.
 */
export const toRampTextureWidth = ({ bands }: ColorScale): number =>
  bands > 0
    ? bands * Math.ceil(RAMP_TEXTURE_WIDTH / bands)
    : RAMP_TEXTURE_WIDTH;

/**
 * Bakes the colour scale into RGBA bytes for upload as a 1D lookup.
 *
 * Baking rather than reimplementing the ramp in WGSL is the point: the shader samples exactly the
 * bytes the software renderer computes, so the two cannot disagree about colour. That covers the
 * ramp steps, the linear-light interpolation between them, and the band quantisation, none of which
 * would be pleasant to keep in sync across two languages - and any drift would be invisible until
 * someone compared screenshots.
 *
 * Sampled at texel centres so the first and last texels are the true ends of the ramp rather than
 * half a step inside it.
 */
export const bakeRampTexture = (colorScale: ColorScale): Uint8Array => {
  const width = toRampTextureWidth(colorScale);
  const pixels = new Uint8Array(width * 4);

  for (let index = 0; index < width; index++) {
    const [r, g, b] = colorScale.at((index + 0.5) / width);
    const offset = index * 4;
    pixels[offset] = r;
    pixels[offset + 1] = g;
    pixels[offset + 2] = b;
    pixels[offset + 3] = 255;
  }

  return pixels;
};
